import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { printService } from 'api/services/printService';
import type { PrintAgent } from 'api/types';

/** Rename a Print Agent and its site. Its key, status and printers are not touched. */
interface Props {
  agent: PrintAgent | null;
  onClose: () => void;
  onSaved: () => void;
}

const PrintAgentEditModal: React.FC<Props> = ({ agent, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: '', site: '' });
  const snapshot = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!agent) return;
    const d = { name: agent.agentName, site: agent.siteName ?? '' };
    setForm(d);
    snapshot.current = JSON.stringify(d);
    setFieldErrors({}); setServerError(null);
  }, [agent]);

  const isDirty = useMemo(() => !!snapshot.current && JSON.stringify(form) !== snapshot.current, [form]);
  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  const handleSave = () => {
    const e: Record<string, string> = {};
    const n = form.name.trim();
    if (n.length < 2 || n.length > 100) e.name = 'The name is 2 to 100 characters, e.g. Kitchen PC.';
    if (form.site.trim().length > 100) e.site = 'At most 100 characters.';
    if (Object.keys(e).length) { setFieldErrors(e); return; }
    setSaveConfirmOpen(true);
  };

  const confirmSave = async () => {
    if (!agent) return;
    setSaveConfirmOpen(false); setIsSaving(true); setServerError(null);
    try {
      await printService.updateAgent(agent.id, { agentName: form.name.trim(), siteName: form.site.trim() || undefined, rowVersion: agent.rowVersion });
      notificationService.toast.success(`"${form.name.trim()}" saved.`);
      setIsSaving(false);
      onSaved(); onClose();
    } catch (err: any) {
      setIsSaving(false);
      setServerError(err?.response?.data?.message ?? 'That could not be saved.');
    }
  };

  if (!agent) return null;
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm" role="dialog" aria-modal="true" style={{ minHeight: 'auto', maxWidth: 480 }} data-agent-edit>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Edit Print Agent — {agent.agentName}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving} aria-label="Close"><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          {serverError && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
          <div className="form-group" data-field="name">
            <label className="form-label" htmlFor="pa-name">Agent name <span className="text-danger">*</span></label>
            <input id="pa-name" className={`form-input${fieldErrors.name ? ' form-field-error' : ''}`} value={form.name} maxLength={100} autoFocus
              onChange={(e) => { setFieldErrors((p) => ({ ...p, name: '' })); setForm((p) => ({ ...p, name: e.target.value })); }} />
            {fieldErrors.name && <div className="form-error-message">{fieldErrors.name}</div>}
          </div>
          <div className="form-group" data-field="site">
            <label className="form-label" htmlFor="pa-site">Site</label>
            <input id="pa-site" className={`form-input${fieldErrors.site ? ' form-field-error' : ''}`} value={form.site} maxLength={100}
              placeholder="e.g., Ground floor kitchen" onChange={(e) => { setFieldErrors((p) => ({ ...p, site: '' })); setForm((p) => ({ ...p, site: e.target.value })); }} />
            {fieldErrors.site && <div className="form-error-message">{fieldErrors.site}</div>}
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || !isDirty} title={!isDirty ? 'No changes to save' : undefined}>Save</button>
        </div>
      </div>
      <ConfirmDialog open={saveConfirmOpen} title="Confirm Save" message={`Save the changes to "${form.name.trim()}"?`}
        confirmLabel="Confirm" cancelLabel="Cancel" onConfirm={() => void confirmSave()} onCancel={() => setSaveConfirmOpen(false)} />
      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

export default PrintAgentEditModal;
